import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { api } from '@/App';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { Plus, Pencil, Trash2, Building2, Package, MapPin } from 'lucide-react';

export default function HospitalsTab() {
  const { t } = useLanguage();
  const [hospitals, setHospitals] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    weekday: '',
    region_id: '',
    allowed_vendor_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [hospitalsRes, vendorsRes, regionsRes] = await Promise.all([
        api.get('/hospitals'),
        api.get('/vendors'),
        api.get('/regions')
      ]);
      setHospitals(hospitalsRes.data);
      setVendors(vendorsRes.data);
      setRegions(regionsRes.data);
    } catch (error) {
      toast.error(t('messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        region_id: formData.region_id || null
      };
      if (editingId) {
        await api.put(`/hospitals/${editingId}`, submitData);
        toast.success(t('hospital.hospitalUpdated'));
      } else {
        await api.post('/hospitals', formData);
        toast.success(t('hospital.hospitalCreated'));
      }
      setOpen(false);
      setFormData({ name: '', weekday: '', allowed_vendor_ids: [] });
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.operationFailed'));
    }
  };

  const handleEdit = (hospital) => {
    setEditingId(hospital.id);
    setFormData({ 
      name: hospital.name, 
      weekday: hospital.weekday,
      region_id: hospital.region_id || '',
      allowed_vendor_ids: hospital.allowed_vendor_ids || []
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('hospital.deleteHospitalConfirm'))) return;
    try {
      await api.delete(`/hospitals/${id}`);
      toast.success(t('hospital.hospitalDeleted'));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.deleteFailed'));
    }
  };

  const toggleVendor = (vendorId) => {
    setFormData(prev => {
      const current = prev.allowed_vendor_ids || [];
      if (current.includes(vendorId)) {
        return { ...prev, allowed_vendor_ids: current.filter(id => id !== vendorId) };
      } else {
        return { ...prev, allowed_vendor_ids: [...current, vendorId] };
      }
    });
  };

  const selectAllVendors = () => {
    setFormData(prev => ({
      ...prev,
      allowed_vendor_ids: vendors.map(v => v.id)
    }));
  };

  const clearAllVendors = () => {
    setFormData(prev => ({
      ...prev,
      allowed_vendor_ids: []
    }));
  };

  const getVendorNames = (vendorIds) => {
    if (!vendorIds || vendorIds.length === 0) return t('hospital.allVendors');
    return vendorIds
      .map(id => vendors.find(v => v.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  if (loading) {
    return <div className="text-center py-8">{t('common.loading')}</div>;
  }

  return (
    <Card className="shadow-lg border-0" data-testid="hospitals-card">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-2xl flex items-center space-x-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              <span>{t('nav.hospitals')}</span>
            </CardTitle>
            <CardDescription>{t('hospital.manageHospitals')}</CardDescription>
          </div>
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              setEditingId(null);
              setFormData({ name: '', weekday: '', allowed_vendor_ids: [] });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg" data-testid="add-hospital-button">
                <Plus className="w-4 h-4 mr-2" />
                {t('hospital.addHospital')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="hospital-dialog">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>{editingId ? t('hospital.editHospital') : t('hospital.addHospital')}</DialogTitle>
                  <DialogDescription>{t('hospital.enterHospitalDetails')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="name">{t('hospital.name')}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., City General Hospital"
                      required
                      data-testid="hospital-name-input"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weekday">{t('hospital.weekday')}</Label>
                    <Select
                      value={formData.weekday}
                      onValueChange={(value) => setFormData({ ...formData, weekday: value })}
                      required
                    >
                      <SelectTrigger className="mt-2" data-testid="hospital-weekday-select">
                        <SelectValue placeholder="Select weekday" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mandag">Mandag</SelectItem>
                        <SelectItem value="Tirsdag">Tirsdag</SelectItem>
                        <SelectItem value="Onsdag">Onsdag</SelectItem>
                        <SelectItem value="Torsdag">Torsdag</SelectItem>
                        <SelectItem value="Fredag">Fredag</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Region Selection */}
                  <div>
                    <Label htmlFor="region" className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4" />
                      <span>Region</span>
                    </Label>
                    <Select
                      value={formData.region_id}
                      onValueChange={(value) => setFormData({ ...formData, region_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className="mt-2" data-testid="hospital-region-select">
                        <SelectValue placeholder="Select region (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No region</SelectItem>
                        {regions.map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Drivers assigned to this region will receive order emails for users at this hospital.
                    </p>
                  </div>
                  
                  {/* Vendor Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="flex items-center space-x-2">
                        <Package className="w-4 h-4" />
                        <span>Allowed Vendors</span>
                      </Label>
                      <div className="flex space-x-2">
                        <Button type="button" variant="outline" size="sm" onClick={selectAllVendors}>
                          Select All
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={clearAllVendors}>
                          Clear All
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      Users at this hospital will only see items from selected vendors. 
                      If none selected, users see all vendors.
                    </p>
                    <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-gray-50">
                      {vendors.length === 0 ? (
                        <p className="text-sm text-gray-500">No vendors available</p>
                      ) : (
                        vendors.map((vendor) => (
                          <div key={vendor.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`vendor-${vendor.id}`}
                              checked={(formData.allowed_vendor_ids || []).includes(vendor.id)}
                              onCheckedChange={() => toggleVendor(vendor.id)}
                              data-testid={`vendor-checkbox-${vendor.id}`}
                            />
                            <Label 
                              htmlFor={`vendor-${vendor.id}`} 
                              className="cursor-pointer text-sm font-normal"
                            >
                              {vendor.name}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="save-hospital-button">
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {hospitals.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No hospitals yet</p>
            <p className="text-sm">Add your first hospital to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hospitals.map((hospital) => (
              <div
                key={hospital.id}
                className="p-4 bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl hover:shadow-lg transition-all"
                data-testid={`hospital-card-${hospital.id}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{hospital.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">Delivery: {hospital.weekday}</p>
                    {hospital.region_id && (
                      <p className="text-sm text-gray-600 flex items-center mt-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        Region: {regions.find(r => r.id === hospital.region_id)?.name || 'Unknown'}
                      </p>
                    )}
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">Allowed Vendors:</p>
                      {(!hospital.allowed_vendor_ids || hospital.allowed_vendor_ids.length === 0) ? (
                        <Badge variant="secondary" className="text-xs">All vendors</Badge>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {hospital.allowed_vendor_ids.slice(0, 3).map(id => {
                            const vendor = vendors.find(v => v.id === id);
                            return vendor ? (
                              <Badge key={id} variant="outline" className="text-xs">
                                {vendor.name}
                              </Badge>
                            ) : null;
                          })}
                          {hospital.allowed_vendor_ids.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{hospital.allowed_vendor_ids.length - 3} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(hospital)}
                      data-testid={`edit-hospital-${hospital.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(hospital.id)}
                      data-testid={`delete-hospital-${hospital.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
